/**
 * Utility functions for measurement data processing
 */

/**
 * Get unit abbreviation for display
 * @param {string} unit - The full unit name
 * @returns {string} - The abbreviated unit
 */
export const getUnitAbbreviation = unit => {
  switch (unit) {
    case 'watts':
      return 'W';
    case 'milliwatts':
      return 'mW';
    case 'kilowatts':
      return 'kW';
    case 'amps':
      return 'A';
    case 'milliamps':
      return 'mA';
    case 'volts':
      return 'V';
    case 'celsius':
      return '°C';
    case 'fahrenheit':
      return '°F';
    case 'kelvin':
      return 'K';
    case 'percent':
      return '%';
    case 'raw_adc':
      return 'ADC';
    default:
      return unit || '';
  }
};

/**
 * Determine if a measurement type refers to relay data
 * @param {string} type - The measurement type
 * @returns {boolean} - True if measurement type is relay-based
 */
export const isRelayMeasurement = type => {
  return type && (type.endsWith('_power') || type.endsWith('_current'));
};

/**
 * Get the base measurement type (without _power or _current suffix)
 * @param {string} type - The measurement type
 * @returns {string} - The base type
 */
export const getBaseMeasurementType = type => {
  if (!type) return type;
  return type.replace(/_(power|current)$/, '');
};

/**
 * Group measurement types that represent the same data (current/power pairs)
 * @param {string[]} types - Array of measurement types
 * @returns {Object} - Grouped types with metadata
 */
export const groupMeasurementTypes = types => {
  const groups = {};

  types.forEach(type => {
    if (isRelayMeasurement(type)) {
      const baseType = getBaseMeasurementType(type);
      const isCurrentType = type.endsWith('_current');
      const isPowerType = type.endsWith('_power');

      if (!groups[baseType]) {
        groups[baseType] = {
          baseType,
          displayType: baseType, // This will be the tab name
          hasCurrentType: false,
          hasPowerType: false,
          currentType: null,
          powerType: null,
          types: [],
        };
      }

      groups[baseType].types.push(type);

      if (isCurrentType) {
        groups[baseType].hasCurrentType = true;
        groups[baseType].currentType = type;
      }
      if (isPowerType) {
        groups[baseType].hasPowerType = true;
        groups[baseType].powerType = type;
      }
    } else {
      // Non-relay measurements get their own group
      groups[type] = {
        baseType: type,
        displayType: type,
        hasCurrentType: false,
        hasPowerType: false,
        currentType: null,
        powerType: null,
        types: [type],
      };
    }
  });

  return groups;
};

/**
 * Convert current to power
 * @param {number} current - Current value in amps
 * @param {number} voltage - Voltage value (default 120V)
 * @returns {number} - Power in watts
 */
export const currentToPower = (current, voltage = 120.0) => {
  return parseFloat(current) * voltage;
};

/**
 * Convert power to current
 * @param {number} power - Power value in watts
 * @param {number} voltage - Voltage value (default 120V)
 * @returns {number} - Current in amps
 */
export const powerToCurrent = (power, voltage = 120.0) => {
  return parseFloat(power) / voltage;
};

/**
 * Convert temperature to Celsius
 * @param {number} value - Temperature value
 * @param {string} fromUnit - Source unit (celsius, fahrenheit, kelvin)
 * @returns {number} - Temperature in Celsius
 */
export const toCelsius = (value, fromUnit) => {
  const numValue = parseFloat(value) || 0;
  switch (fromUnit) {
    case 'celsius':
    case 'celcius': // Handle API typo
      return numValue;
    case 'fahrenheit':
      return ((numValue - 32.0) * 5.0) / 9.0;
    case 'kelvin':
      return numValue - 273.15;
    default:
      return numValue;
  }
};

/**
 * Convert temperature to Fahrenheit
 * @param {number} value - Temperature value
 * @param {string} fromUnit - Source unit (celsius, fahrenheit, kelvin)
 * @returns {number} - Temperature in Fahrenheit
 */
export const toFahrenheit = (value, fromUnit) => {
  const numValue = parseFloat(value) || 0;
  switch (fromUnit) {
    case 'fahrenheit':
      return numValue;
    case 'celsius':
      return (numValue * 9.0) / 5.0 + 32.0;
    case 'kelvin':
      return ((numValue - 273.15) * 9.0) / 5.0 + 32.0;
    default:
      return numValue;
  }
};

/**
 * Determine if a measurement type refers to sensor data
 * @param {string} type - The measurement type
 * @returns {boolean} - True if measurement type is sensor-based
 */
export const isSensorMeasurement = type => {
  return type && (type === 'temperature' || type === 'humidity' || type === 'pressure');
};

/**
 * Get display label for measurement type
 * @param {string} type - The measurement type
 * @param {function} t - Translation function (optional)
 * @returns {string} - Human readable label
 */
export const getMeasurementTypeLabel = (type, t) => {
  if (!type) return t ? t('measurements.unknown') : 'Unknown';

  if (type.endsWith('_power') || type.endsWith('_current')) {
    const baseType = getBaseMeasurementType(type);
    if (t) {
      // Use translation keys for power/current types - use the base type for the tab
      const translationKey = `measurements.types.${baseType}`;
      return t(translationKey, { defaultValue: `${baseType.replace('_', ' ').toUpperCase()}` });
    }
    return baseType.replace('_', ' ').toUpperCase();
  }

  if (t) {
    // Use translation keys for other measurement types
    const translationKey = `measurements.types.${type}`;
    return t(translationKey, { defaultValue: type.replace('_', ' ').toUpperCase() });
  }

  // Add more type mappings as needed
  return type.replace('_', ' ').toUpperCase();
};
